import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  MessageSquare, Mail, MailOpen, Reply, Clock, CheckCircle, 
  AlertCircle, User, Briefcase, Filter, Send, X, Loader2 
} from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

export default function StaffMessages() {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, read: 0, replied: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, [statusFilter]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await apiClient.get(`/api/notifications/staff-messages${params}`);
      setMessages(response.data.messages || []);
      setCounts(response.data.counts || { pending: 0, read: 0, replied: 0, total: 0 });
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await apiClient.put(`/api/notifications/staff-messages/${messageId}/read`);
      fetchMessages();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    setReplying(true);
    try {
      await apiClient.post(`/api/notifications/staff-messages/${selectedMessage.id}/reply`, {
        reply: replyText
      });
      toast.success('Reply sent successfully!');
      setSelectedMessage(null);
      setReplyText('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Query': return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'Request': return <Send className="h-5 w-5 text-green-500" />;
      case 'Complaint': return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'Feedback': return <CheckCircle className="h-5 w-5 text-purple-500" />;
      default: return <Mail className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Pending':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Pending</span>;
      case 'Read':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Read</span>;
      case 'Replied':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Replied</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Messages</h1>
          <p className="text-sm text-gray-500 mt-1">View and respond to messages from staff</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`cursor-pointer transition-all ${statusFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setStatusFilter('all')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Messages</p>
                <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
              </div>
              <Mail className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${statusFilter === 'Pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setStatusFilter('Pending')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${statusFilter === 'Read' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setStatusFilter('Read')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Read</p>
                <p className="text-2xl font-bold text-blue-600">{counts.read}</p>
              </div>
              <MailOpen className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${statusFilter === 'Replied' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setStatusFilter('Replied')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Replied</p>
                <p className="text-2xl font-bold text-green-600">{counts.replied}</p>
              </div>
              <Reply className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Messages List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Messages
            {statusFilter !== 'all' && (
              <span className="text-sm font-normal text-gray-500">
                (Filtered: {statusFilter})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No messages found</p>
              <p className="text-sm">Staff messages will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors ${
                    message.status === 'Pending' ? 'border-yellow-200 bg-yellow-50/50' : ''
                  }`}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (message.status === 'Pending') {
                      markAsRead(message.id);
                    }
                  }}
                >
                  <div className="flex items-start gap-4">
                    {getTypeIcon(message.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{message.staff_name}</span>
                          <span className="text-xs text-gray-500 font-mono">({message.employee_id})</span>
                          {getStatusBadge(message.status)}
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(message.created_at)}</span>
                      </div>
                      <p className="font-medium text-gray-800">{message.subject || '(No Subject)'}</p>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">{message.message}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{message.type}</span>
                        {message.status === 'Replied' && message.admin_reply && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <Reply className="h-3 w-3" /> Reply sent
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-3">
                {getTypeIcon(selectedMessage.type)}
                <div>
                  <h3 className="font-bold text-gray-900">{selectedMessage.subject || '(No Subject)'}</h3>
                  <p className="text-sm text-gray-500">
                    From: {selectedMessage.staff_name} ({selectedMessage.employee_id})
                  </p>
                </div>
              </div>
              <button onClick={() => setSelectedMessage(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Message Info */}
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {formatDate(selectedMessage.created_at)}
                </span>
                <span className="px-2 py-0.5 bg-gray-100 rounded">{selectedMessage.type}</span>
                {getStatusBadge(selectedMessage.status)}
              </div>

              {/* Message Content */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-800 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>

              {/* Existing Reply */}
              {selectedMessage.status === 'Replied' && selectedMessage.admin_reply && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Reply className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-800">Your Reply</span>
                    <span className="text-xs text-green-600">
                      ({selectedMessage.replied_by} - {formatDate(selectedMessage.replied_at)})
                    </span>
                  </div>
                  <p className="text-green-800">{selectedMessage.admin_reply}</p>
                </div>
              )}

              {/* Reply Form */}
              {selectedMessage.status !== 'Replied' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Send Reply</label>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply here..."
                    rows={4}
                  />
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setSelectedMessage(null)}>
                      Close
                    </Button>
                    <Button onClick={handleReply} disabled={replying} className="bg-green-600 hover:bg-green-700">
                      {replying ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                      ) : (
                        <><Reply className="h-4 w-4 mr-2" /> Send Reply</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
